import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Changing this key resets the boundary -- used to recover automatically
   * when the user navigates to a different page after a crash. */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

/**
 * The blank-Queues-page bug (a Decimal-serialized-as-string field crashing
 * `.toFixed()` on render) unmounted the ENTIRE app with no trace on screen,
 * because nothing in the frontend ever caught a render error -- confirmed by
 * grepping for ErrorBoundary before this file existed: zero hits. Fixed at
 * its source (app/api/routes/queues.py now casts the NUMERIC columns to
 * float8), but that fix only closes THIS instance of the bug class. This
 * boundary is the backstop for the next one: an honest "this page hit an
 * error" message instead of a silent blank screen, matching architecture's
 * own "surface, don't hide" principle -- an empty or broken state should be
 * SHOWN, never indistinguishable from the app simply not having loaded.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught a render error:", error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "3rem 2rem", textAlign: "center", color: "var(--ink-2, #666)" }}>
          <div style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "0.5rem" }}>
            This page hit an error and couldn't render.
          </div>
          <div style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
            {this.state.error.message || String(this.state.error)}
          </div>
          <div style={{ fontSize: "0.85rem" }}>
            Try another tab, or reload. The rest of the app is unaffected.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
